import { apiRequest } from '@scc/lib'
import type { CalendarRepository } from '@/data/repositories/types'
import type {
  CalendarEvent, CalendarEventInput, CampaignType,
  ProductItem, ChecklistItem, BudgetLine, CrmSegment,
} from '@/modules/calendar/types'

interface ApiCalendarEvent {
  id: string
  storeId: number
  date: string
  endDate: string | null
  title: string
  type: CampaignType
  owner: string
  notes: string
  products: ProductItem[]
  promoDetails: string
  incentive: boolean
  creatorNotes: string
  seedingTimeline: string
  imageStack: ChecklistItem[]
  productBrief: ChecklistItem[]
  assetNotes: string
  crmMessageNeeded: boolean
  crmSegments: CrmSegment[]
  crmSendDate: string | null
  messageWritten: boolean
  scheduled: boolean
  promotionScheduled: boolean
  budget: BudgetLine[]
  draftDueDate: string | null
  updatedBy: string
  createdAt: string
  updatedAt: string
}

export function mapEvent(row: ApiCalendarEvent): CalendarEvent {
  const event: CalendarEvent = {
    id: row.id,
    clientId: String(row.storeId),
    date: row.date,
    title: row.title,
    type: row.type,
    owner: row.owner,
    notes: row.notes,
    products: row.products ?? [],
    promoDetails: row.promoDetails ?? '',
    incentive: row.incentive ?? false,
    creatorNotes: row.creatorNotes ?? '',
    seedingTimeline: row.seedingTimeline ?? '',
    imageStack: row.imageStack ?? [],
    productBrief: row.productBrief ?? [],
    assetNotes: row.assetNotes ?? '',
    crmMessageNeeded: row.crmMessageNeeded ?? false,
    crmSegments: row.crmSegments ?? [],
    crmSendDate: row.crmSendDate ?? null,
    messageWritten: row.messageWritten ?? false,
    scheduled: row.scheduled ?? false,
    promotionScheduled: row.promotionScheduled ?? false,
    budget: row.budget ?? [],
    draftDueDate: row.draftDueDate ?? null,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    updatedBy: row.updatedBy,
  }
  if (row.endDate) event.endDate = row.endDate
  return event
}

function basePath(storeId: string): string {
  return `/clients/${encodeURIComponent(storeId)}/calendar/events`
}

export function createApiCalendarRepository(): CalendarRepository {
  return {
    async getEvents(_orgId, clientId) {
      const rows = await apiRequest<ApiCalendarEvent[]>(basePath(clientId))
      return rows.map(mapEvent)
    },
    async createEvent(_orgId, clientId, input: CalendarEventInput, actor) {
      const row = await apiRequest<ApiCalendarEvent>(basePath(clientId), {
        method: 'POST',
        body: { ...input, actor },
      })
      return mapEvent(row)
    },
    async updateEvent(_orgId, clientId, id, patch, actor) {
      const row = await apiRequest<ApiCalendarEvent>(
        `${basePath(clientId)}/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: { ...patch, actor } },
      )
      return mapEvent(row)
    },
    async deleteEvent(_orgId, clientId, id) {
      await apiRequest<null>(
        `${basePath(clientId)}/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      )
    },
  }
}

export const calendarRepository = createApiCalendarRepository()
